import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, User, CreditCard, AlertTriangle } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from "@/hooks/use-mobile";
import { usernameValidationService } from '@/services/usernameValidationService';
import PaymentMethodsModal from '@/components/monetization/PaymentMethodsModal';
import BillingAddressModal from '@/components/monetization/BillingAddressModal';
import SubscriptionHistoryModal from '@/components/monetization/SubscriptionHistoryModal';
import SubscriptionOptionsModal from '@/components/monetization/SubscriptionOptionsModal';
import AccountUpgradeModal from '@/components/monetization/AccountUpgradeModal';
import NotificationPreferencesSection from '@/components/notifications/NotificationPreferencesSection';

const AccountSettings = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const user = supabaseUser || internalUser;
  const isInternalUser = !!internalUser && !supabaseUser;
  const currentUsername = isInternalUser ? internalUser?.username : profile?.username;
  
  const [newUsername, setNewUsername] = useState(currentUsername || '');
  const [usernameUnderstanding, setUsernameUnderstanding] = useState(false);
  const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  // Modal states
  const [paymentMethodsOpen, setPaymentMethodsOpen] = useState(false);
  const [billingAddressOpen, setBillingAddressOpen] = useState(false);
  const [subscriptionHistoryOpen, setSubscriptionHistoryOpen] = useState(false);
  const [subscriptionOptionsOpen, setSubscriptionOptionsOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [historyView, setHistoryView] = useState<'renewals' | 'invoices'>('renewals');
  const [optionsAction, setOptionsAction] = useState<'downgrade' | 'cancel'>('downgrade');

  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleUsernameChange = async () => {
    if (!usernameUnderstanding) {
      toast.error('Please confirm you understand the username change policy');
      return;
    }

    if (newUsername === currentUsername) {
      toast.error('Please enter a different username');
      return;
    }

    setIsUpdatingUsername(true);
    setUsernameError('');

    try {
      // Validate username
      const validation = await usernameValidationService.validateUsername(newUsername, currentUsername);
      
      if (!validation.isValid) {
        setUsernameError(validation.message || 'Invalid username');
        return;
      }

      // Here you would implement the actual username update logic
      // This would depend on whether it's a Supabase user or internal user
      
      toast.success('Username updated successfully');
      setUsernameUnderstanding(false);
    } catch (error) {
      console.error('Error updating username:', error);
      toast.error('Failed to update username');
    } finally {
      setIsUpdatingUsername(false);
    }
  };

  const isPaidAccount = () => {
    const paidRoles = ['coach', 'team-owner', 'venue'];
    const userRole = isInternalUser ? internalUser?.role : profile?.role;
    return paidRoles.includes(userRole || '');
  };

  const getUserRole = () => {
    return isInternalUser ? internalUser?.role : profile?.role;
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap = {
      'coach': 'Coach',
      'team-owner': 'Team Owner',
      'venue': 'Venue Manager',
      'player': 'Player',
      'fan': 'Fan',
      'parent': 'Parent',
      'creative': 'Creative',
      'support': 'Support'
    };
    return roleMap[role] || role;
  };

  const getUserType = (): 'supabase' | 'internal' => {
    return isInternalUser ? 'internal' : 'supabase';
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="hidden md:block">
        <Header />
      </div>
      <div className="block md:hidden">
        <MobileHeader />
      </div>
      
      <main className="pt-20 pb-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline" 
              className="border-gray-600 text-gray-400 hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
            <p className="text-gray-400">Manage your account information and preferences</p>
          </div>

          <div className="space-y-6">
            {/* Username Change Section */}
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                  <User className="h-6 w-6" />
                  Username Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="current-username" className="text-gray-300">Current Username</Label>
                  <div className="mt-1 p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <span className="text-white">{currentUsername || 'Not set'}</span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="new-username" className="text-gray-300">New Username</Label>
                  <Input
                    id="new-username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white"
                    placeholder="Enter new username"
                  />
                  {usernameError && (
                    <p className="text-red-400 text-sm mt-1">{usernameError}</p>
                  )}
                </div>

                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-yellow-400 font-medium mb-2">Important Notice</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        If you change your username, your current username will be available for anyone else to claim immediately. 
                        We do not hold or reserve usernames once they are changed. Please ensure you understand this policy before proceeding.
                      </p>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="username-understanding"
                          checked={usernameUnderstanding}
                          onCheckedChange={(checked) => setUsernameUnderstanding(checked === true)}
                        />
                        <Label htmlFor="username-understanding" className="text-sm text-gray-300 cursor-pointer">
                          I understand and accept the username change policy
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleUsernameChange}
                  disabled={!newUsername || !usernameUnderstanding || isUpdatingUsername || newUsername === currentUsername}
                  className="bg-sports-green hover:bg-sports-green/90 text-black"
                >
                  {isUpdatingUsername ? 'Updating...' : 'Update Username'}
                </Button>
              </CardContent>
            </Card>

            {/* Subscription Management - Only for Paid Accounts */}
            {isPaidAccount() && (
              <Card className="bg-gray-900/50 border border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                    <CreditCard className="h-6 w-6" />
                    Subscription Management
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Current Plan */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-3">Current Plan</h3>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-sports-green border-sports-green">
                        {getRoleDisplayName(getUserRole() || '')} Plan
                      </Badge>
                      <span className="text-gray-300">Active</span>
                    </div>
                  </div>

                  <Separator className="bg-gray-700" />

                  {/* Billing Information */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-3">Billing Information</h3>
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => setPaymentMethodsOpen(true)}
                      >
                        Manage Payment Methods
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => setBillingAddressOpen(true)}
                      >
                        Update Billing Address
                      </Button>
                    </div>
                  </div>

                  <Separator className="bg-gray-700" />

                  {/* Subscription History */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Subscription History
                    </h3>
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => {
                          setHistoryView('renewals');
                          setSubscriptionHistoryOpen(true);
                        }}
                      >
                        View Renewal Dates
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => {
                          setHistoryView('invoices');
                          setSubscriptionHistoryOpen(true);
                        }}
                      >
                        Download Previous Invoices
                      </Button>
                    </div>
                  </div>

                  <Separator className="bg-gray-700" />

                  {/* Subscription Options */}
                  <div>
                    <h3 className="text-lg font-medium text-white mb-3">Subscription Options</h3>
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => {
                          setOptionsAction('downgrade');
                          setSubscriptionOptionsOpen(true);
                        }}
                      >
                        Downgrade Plan
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-red-600 text-red-400 hover:bg-red-900/20"
                        onClick={() => {
                          setOptionsAction('cancel');
                          setSubscriptionOptionsOpen(true);
                        }}
                      >
                        Cancel Subscription
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Account Upgrade - Only for Free Accounts */}
            {!isPaidAccount() && (
              <Card className="bg-gray-900/50 border border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                    <CreditCard className="h-6 w-6" />
                    Account Upgrade
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-400">
                    Upgrade your account to unlock premium features and advanced functionality.
                  </p>
                  <Button
                    onClick={() => setUpgradeModalOpen(true)}
                    className="bg-sports-green hover:bg-sports-green/90 text-black"
                  >
                    View Upgrade Options
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Notification Preferences */}
            <NotificationPreferencesSection />
          </div>
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />

      {/* Modals */}
      <PaymentMethodsModal
        isOpen={paymentMethodsOpen}
        onClose={() => setPaymentMethodsOpen(false)}
        userType={getUserType()}
      />

      <BillingAddressModal
        isOpen={billingAddressOpen}
        onClose={() => setBillingAddressOpen(false)}
        userType={getUserType()}
      />

      <SubscriptionHistoryModal
        isOpen={subscriptionHistoryOpen}
        onClose={() => setSubscriptionHistoryOpen(false)}
        userType={getUserType()}
        view={historyView}
      />

      <SubscriptionOptionsModal
        isOpen={subscriptionOptionsOpen}
        onClose={() => setSubscriptionOptionsOpen(false)}
        userType={getUserType()}
        action={optionsAction}
        currentRole={getUserRole()}
      />

      <AccountUpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        currentRole={getUserRole() || 'player'}
      />
    </div>
  );
};

export default AccountSettings;
